<template>
    <div id="background">
        <h6 class="page-title">Retention Assessment Background</h6>
        <h4 class="tab-main-title">Introduction</h4>
        <div class="lost-revenue-copy">
            <p>
                When a dealership loses an experienced Technician, there are cascading impacts on revenue and profits. When Technicians leave, they are often replaced with new talent who aren’t as productive. Investing in systems and processes to keep Technicians happy may seem like a risky investment for dealerships under serious financial pressure. However, these investments actually lead to significant long-term savings and help fuel revenue growth.
            </p>

            <p>
                This tool is designed to help you help your Dealers improve Technician retention. When you complete a dealership assessment, your Dealer Principal and Service Manager will receive a report grading their performance in five key areas known to impact retention (Compensation, Training, Management, Dealer Conditions, Rewards & Recognition). Dealers can use the report findings to pick an area of focus and create an Action Plan to begin the improvement process.
            </p>

            <p class="mb-0">
                Set the stage with your Dealer. Review the tabbed sections below, and complete the Assessment Form with your Dealer to get started.
            </p>
        </div>
        <div class="lost-revenue" v-if="show_calculator">
            <p class="lost-revenue-title">ESTIMATED TOTAL ANNUAL LOST REVENUE DUE TO TECHNICIAN TURNOVER:</p>

            <div class="lost-revenue-equation mb-5">
                <div class="equation-flex-direction">
                    <div class="left">
                        <span class="left-num">${{num_decrease}}k*</span>
                        <p class="equation-bottom-text">Value of Decreasing Turnover by 1%</p>
                    </div>
                    <div class="center">
                        <div class="c-top">
                            <span class="equation-symbol"><font-awesome-icon class="fa-fw" :icon="['far', 'times']" /></span>
                            <b-input-group>
                                <b-input-group-prepend>
                                    <b-button variant="outline-secondary" @click="dec_percent"><font-awesome-icon class="fa-fw" :icon="['fal', 'arrow-circle-down']" /></b-button>
                                </b-input-group-prepend>

                                <b-form-input type="number" min="0" max="99" v-model="num_percent" @change="calculateTurnover"></b-form-input>

                                <b-input-group-append>
                                    <b-button variant="outline-secondary" @click="inc_percent"><font-awesome-icon class="fa-fw" :icon="['fal', 'arrow-circle-up']" /></b-button>
                                </b-input-group-append>
                            </b-input-group>
                        </div>
                        <p class="equation-bottom-text">Enter Dealership's Annual Turnover Rate</p>
                    </div>
                    <div class="right">
                        <div class="r-top">
                            <span class="equation-symbol"><font-awesome-icon class="fa-fw" :icon="['far', 'equals']" /></span>
                            <span class="right-num">${{parseFloat(num_turnover).toString()}}k</span>
                        </div>
                        <p class="equation-bottom-text">Estimated Total Annual Lost Revenue Due to Technician Turnover</p>
                    </div>
                </div>
            </div>

            <small>*Decreasing Technician turnover by 1% increases annual revenue per Technician by $1700, Carlisle & Co. Technician Retention How-to Guide 2020.</small>
        </div>

        <div class="mt-5">
            <h4 class="tab-main-title">BENCHMARKS</h4>
            <div>
<!--                <mq-layout mq="lg+">-->
              <div>
                    <b-tabs fill>
                        <b-tab title="Compensation" active>
                            <b-card-text class="text-left">
                                <h6 class="tab-heading">Impact of Improving Compensation on Technician Retention</h6>
                                <div class="row">
                                    <div class="col-md-4">
                                        <p>
                                            People are driven to become Technicians based on their passion for fixing vehicles. They see the career as an opportunity to do what they love, coupled with the potential for high earnings. Students are taught in technical schools that quality Technicians can earn over six figures annually. And although this is technically true, little context is provided regarding the time it actually takes to achieve the efficiency required to earn this level of compensation.
                                        </p>
                                    </div>
                                    <div class="col-md-8">
                                        <div class="chart-container">
                                            <img width="100%" src='../assets/images/compensation1.png' class="" alt="chart">
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-4">
                                    <p>
                                        In reality, most new Technicians are not efficient enough to turn hours and generate this income. In fact, they often struggle to make a livable wage when thrown onto flat rate too early. As stress builds and initial expectations of their salary are not met, recent hires leave the field with no desire to return.
                                    </p>
                                    <p>
                                        <strong>Recommendations</strong> in this section focus on decreasing the pain Technicians feel from issues with compensation. To correct existing problems, we suggest focusing on the following areas to ensure that plans are robust enough to attract and retain talent in the first 12-18 months:
                                    </p>
                                    <ul>
                                        <li>
                                            <strong>Annual Income:</strong> Create a competitive pay plan for Technicians in terms of annual income
                                        </li>
                                        <li>
                                            <strong>Pay Plan Methodology and Bonus Structure:</strong> Institute a method for compensating Technicians that involves guaranteed pay for new individuals, providing support during the transition to flat rate pay plans
                                        </li>
                                        <li>
                                            <strong>Benefits:</strong> Incorporate benefits, such as healthcare and retirement matching, to create a holistic compensation package for Technicians
                                        </li>
                                    </ul>
                                </div>
                            </b-card-text>
                        </b-tab>
                        <b-tab title="Training">
                            <b-card-text class="text-left">
                                <h6 class="tab-heading">Impact of Improving Training on Technician Retention</h6>
                                <div class="row">
                                    <div class="col-md-5">
                                        <p>
                                            Training opportunities are a significant factor in attracting Technicians to dealership positions and keeping them satisfied in these roles. Training is essential for two primary reasons:
                                        </p>
                                        <p class="pl-4">
                                            <strong>Increases Technician Earnings</strong><br/>
                                            Through training, Technicians become more efficient; this leads them to generate higher earnings in the long run, resulting in more satisfied employees and higher retention.
                                        </p>
                                        <p class="pl-4">
                                            <strong>Improves Sense of Value</strong><br/>
                                            Robust and supportive training programs are a clear investment by dealerships in their personnel, demonstrating the importance Managers place on their people.
                                        </p>
                                    </div>
                                    <div class="col-md-7">
                                        <div class="chart-container">
                                            <img width="100%" src='../assets/images/training1.png' class="" alt="chart">
                                        </div>
                                    </div>
                                </div>
                                <p>
                                    Technicians seek out employers who offer training opportunities, as they see this as a clear path to increasing their earnings over time. Dealerships that do not offer these programs, or who do not meet the expectations of individuals, usually face high turnover as people see no path to upward mobility.
                                </p>
                                <p>
                                    <strong>Recommendations</strong><br/>
                                    Dealerships must have structured training programs to adequately support their Technicians. This section outlines the following items to assist dealerships in improving their training programs:
                                </p>
                                <ul>
                                    <li>
                                        <strong>Onboarding Process:</strong> A defined process for onboarding new Technicians into the shop
                                    </li>
                                    <li>
                                        <strong>Training Class Policies:</strong> Tracking, waitlisting, and sending Technicians to Instructor-Led Trainings
                                    </li>
                                    <li>
                                        <strong>Career Planning:</strong> Clear steps to understand career progression and track goals
                                    </li>
                                    <li>
                                        <strong>Mentorship Programs:</strong> An outlined framework for mentors training apprentices
                                    </li>
                                </ul>
                            </b-card-text>
                        </b-tab>
                        <b-tab title="Management">
                            <b-card-text class="text-left">
                                <h6 class="tab-heading">Impact of Improving Management on Technician Retention</h6>
                                <div class="row">
                                    <div class="col-md-4">
                                        <p>
                                            While the responsibilities between distinct management roles differ, their combined efforts significantly affect Technician satisfaction. Management at every level of the organization has a role in cultivating a supportive environment in which Technicians feel valued.
                                        </p>
                                        <p>
                                            For new Technicians, high-quality Managers are essential for building confidence and providing a positive first impression of the industry. First impressions are critical, and Technicians who have negative experiences early in their careers are more likely to leave for a position in another industry. As a result, the responsibility falls on Managers to create a rewarding experience from the very beginning.
                                        </p>
                                    </div>
                                    <div class="col-md-8">
                                        <div class="chart-container">
                                            <img width="100%" src='../assets/images/management1.png' class="" alt="chart">
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-4">
                                    <p>
                                        <strong>Recommendations</strong> in this section focus on creating positive, genuine relationships between Service Managers and Technicians, as well as developing a framework to correct existing issues.  We recommend evaluating a dealership’s performance by focusing on the following areas:
                                    </p>
                                    <ul>
                                        <li>
                                            <strong>Meeting Structure:</strong> Format and frequency of Dealership Meetings for all Technicians, aimed toward fostering positive relationships with Managers
                                        </li>
                                        <li>
                                            <strong>Error Policy:</strong> Procedures on how to handle errors in the dealership, maintaining respect for Technicians while still addressing concerns
                                        </li>
                                        <li>
                                            <strong>Feedback Mechanisms:</strong> Systems to request and receive feedback from Technicians
                                        </li>
                                    </ul>
                                </div>
                            </b-card-text>
                        </b-tab>
                        <b-tab title="Dealer Conditions">
                            <b-card-text class="text-left">
                                <h6 class="tab-heading">Impact of Improving Dealer Conditions on Technician Retention</h6>
                                <p>
                                    Technicians are influenced heavily by the working conditions at a dealership. Working conditions refer to two factors specifically:
                                </p>
                                <p class="pl-4">
                                    <strong>Physical Workspace:</strong> The facility, equipment and tools that Technicians are provided within a dealership (e.g., breakroom and dealership cleanliness).
                                </p>
                                <p class="pl-4">
                                    <strong>Dealership Culture:</strong> The environment and atmosphere that makes up a Service Department (e.g., relationships with co-workers).
                                </p>
                                <div class="row">
                                    <div class="col-md-6">
                                        <p>
                                            Dealerships may not recognize how critical these items are in attracting and retaining talent; however, the numbers illustrate their importance. 29% of all Technicians at dealerships selected their position because of the culture, while 10% join because of the quality of facility.
                                        </p>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="chart-container">
                                            <img width="100%" src='../assets/images/dealerconditions1.png' class="" alt="chart">
                                        </div>
                                    </div>
                                </div>
                                <p>
                                    <strong>Recommendations</strong> in this section emphasize improving the physical and cultural conditions of a dealership. We recommend that Managers evaluate the following areas:
                                </p>
                                <ul>
                                    <li>
                                        <strong>Dispatch Methods:</strong> Methods for distributing work to Technicians
                                    </li>
                                    <li>
                                        <strong>Work Processes:</strong> Processes to maximize Technician productivity by increasing the amount of time an individual spends in their bay
                                    </li>
                                    <li>
                                        <strong>Facilities Standards:</strong> Dealership guidelines for cleanliness and organization
                                    </li>
                                    <li>
                                        <strong>Dealerships Culture:</strong> The culture of dealerships and relationships between the Service Department personnel
                                    </li>
                                </ul>
                            </b-card-text>
                        </b-tab>
                        <b-tab title="Rewards & Recognition">
                            <b-card-text class="text-left">
                                <h6 class="tab-heading">Impact of Improving Rewards and Recognition on Technician Retention</h6>
                                <div class="row">
                                    <div class="col-md-5">
                                        <p>
                                            Many dealerships have formal recognition programs for experienced Technicians. However, few dealerships have policies in which new hires are eligible. Since the turnover of Technicians within the first 12-18 months is a critical issue, it is important to institute policies to recognize these individuals for their efforts.
                                        </p>
                                        <p>
                                            One in five Technicians selects dealerships based on how Managers treat them.
                                        </p>
                                    </div>
                                  <div class="col-md-7">
                                    <div class="chart-container">
                                      <img width="100%" src='../assets/images/rewards1.png' class="" alt="chart">
                                    </div>
                                  </div>
                                </div>
                                <div class="mt-4">
                                    <p>
                                        <strong>Recommendations</strong> in this section focus on establishing effective rewards and recognition programs. Through extensive industry research, we have uncovered programs that are effective and the reasons why Technicians care.  We recommend that dealerships focus on the following areas:
                                    </p>
                                    <ul>
                                        <li>
                                            <strong>Rewards Programs and Special Bonuses:</strong> Initiatives and incentives that focus on acknowledging Technicians, as well as providing awards for their contribution to the dealership
                                        </li>
                                        <li>
                                            <strong>Workday Perks:</strong> Incentives offered to staff and communicate that Managers appreciate their work
                                        </li>
                                        <li>
                                            <strong>Tenure Benefits:</strong> Policies to acknowledge veteran Technicians for their commitment to the dealership and industry
                                        </li>
                                    </ul>
                                </div>
                            </b-card-text>
                        </b-tab>
                    </b-tabs>
<!--                </mq-layout>-->
              </div>
<!--                <mq-layout :mq="['sm', 'md']">-->
<!--                    <div class="accordion" role="tablist" ref="test">-->
<!--                        <b-card no-body class="mb-2">-->
<!--                            <b-card-header v-b-toggle.accordion-1 header-tag="header" class="p-1" role="tab">-->
<!--                                    <span>Compensation</span>-->
<!--                            </b-card-header>-->
<!--                            <b-collapse id="accordion-1" visible accordion="my-accordion" role="tabpanel">-->
<!--                                <b-card-body>-->
<!--                                    <b-card-text class="text-left">-->
<!--                                        <h6 class="accordion-heading">Impact of Improving Training on Annual Technician Revenue</h6>-->
<!--                                        <div class="row">-->
<!--                                            <div class="col-md-6">-->
<!--                                                <p>-->
<!--                                                    People are driven to become Technicians based on their passion for fixing vehicles. They see the career as an opportunity to do what they love, coupled with the potential for high earnings. Students are taught in Technical schools that quality Technicians can earn over six figures annually. And although this is Technically true, little context is provided regarding the time it actually takes to achieve the efficiency required to earn this level of compensation.-->
<!--                                                </p>-->
<!--                                            </div>-->
<!--                                            <div class="col-md-6">-->
<!--                                                <div class="chart-container">-->
<!--                                                    <img width="100%" src='../assets/images/chart-placeholder.png' class="" alt="chart">-->
<!--                                                </div>-->
<!--                                            </div>-->
<!--                                        </div>-->
<!--                                        <div class="mt-4">-->
<!--                                            <p>-->
<!--                                                In reality, most new Technicians are not efficient enough to turn hours and generate this income. In fact, they often struggle to make a livable wage when thrown onto flat rate too early. As stress builds and initial expectations of their salary are not met, recent hires leave the field with no desire to return.-->
<!--                                            </p>-->
<!--                                            <p>-->
<!--                                                <strong>Recommendations</strong> in this section focus on decreasing the pain Technicians feel from issues with compensation. To correct existing problems, we suggest focusing on the following areas to ensure that plans are robust enough to attract and retain talent in the first 12- 18 months:-->
<!--                                            </p>-->
<!--                                            <ul>-->
<!--                                                <li>-->
<!--                                                    <strong>Annual Income:</strong> Create a competitive pay plan for Technicians in terms of annual income-->
<!--                                                </li>-->
<!--                                                <li>-->
<!--                                                    <strong>Pay Plan Methodology and Bonus Structure:</strong> Institute a method for compensating Technicians that involves guaranteed pay for new individuals, providing support during the transition to flat rate pay plans-->
<!--                                                </li>-->
<!--                                                <li>-->
<!--                                                    <strong>Benefits:</strong> Incorporate benefits, such as healthcare and retirement matching, to create a holistic compensation package for Technicians-->
<!--                                                </li>-->
<!--                                            </ul>-->
<!--                                        </div>-->
<!--                                    </b-card-text>-->
<!--                                </b-card-body>-->
<!--                            </b-collapse>-->
<!--                        </b-card>-->

<!--                        <b-card no-body class="mb-2">-->
<!--                            <b-card-header v-b-toggle.accordion-2 header-tag="header" class="p-1" role="tab">-->
<!--                                <span>Training</span>-->
<!--                            </b-card-header>-->
<!--                            <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel">-->
<!--                                <b-card-body>-->
<!--                                    <b-card-text class="text-left">-->
<!--                                        <h6 class="accordion-heading">Impact of Improving Training on Annual Technician Revenue</h6>-->
<!--                                        <div class="row">-->
<!--                                            <div class="col-md-6">-->
<!--                                                <p>-->
<!--                                                    Training opportunities are a significant factor attracting Technicians to dealership positions and keeping them satisfied in these roles. Training is essential for two primary reasons:-->
<!--                                                </p>-->
<!--                                                <p class="pl-4">-->
<!--                                                    <strong>Increases Technician Earnings</strong><br/>-->
<!--                                                    Through training, Technicians become more efficient; this leads them to generate higher earnings in the long run, resulting in more satisfied employees and higher retention.-->
<!--                                                </p>-->
<!--                                                <p class="pl-4">-->
<!--                                                    <strong>Improves Sense Of Value</strong><br/>-->
<!--                                                    Robust and supportive training programs are a clear investment by dealerships into their personnel, demonstrating the importance Managers place on their people.-->
<!--                                                </p>-->
<!--                                            </div>-->
<!--                                            <div class="col-md-6">-->
<!--                                                <div class="chart-container">-->
<!--                                                    <img width="100%" src='../assets/images/chart-placeholder.png' class="" alt="chart">-->
<!--                                                </div>-->
<!--                                            </div>-->
<!--                                        </div>-->
<!--                                        <p>-->
<!--                                            Technicians seek out employers who offer training opportunities, as they see this as a clear path to increasing their earnings over time. Dealerships that do not offer these programs, or who do not meet the expectations of individuals, usually face high turnover as people see no path to upward mobility.-->
<!--                                        </p>-->
<!--                                        <p>-->
<!--                                            <strong>Recommendations</strong><br/>-->
<!--                                            Dealerships must have structured training programs to adequately support their Technicians. This section outlines the following items to assist dealerships in improving their training programs:-->
<!--                                        </p>-->
<!--                                        <ul>-->
<!--                                            <li>-->
<!--                                                <strong>Onboarding Process:</strong> A defined process for onboarding new Technicians into the shop-->
<!--                                            </li>-->
<!--                                            <li>-->
<!--                                                <strong>Training Class Policies:</strong> Tracking, registering, and sending Technicians to instructor-led trainings-->
<!--                                            </li>-->
<!--                                            <li>-->
<!--                                                <strong>Career Planning:</strong> Clear steps to understand career progression and track goals-->
<!--                                            </li>-->
<!--                                            <li>-->
<!--                                                <strong>Mentorship Programs:</strong> An outlined framework for mentors training apprentices-->
<!--                                            </li>-->
<!--                                        </ul>-->
<!--                                    </b-card-text>-->
<!--                                </b-card-body>-->
<!--                            </b-collapse>-->
<!--                        </b-card>-->

<!--                        <b-card no-body class="mb-2">-->
<!--                            <b-card-header v-b-toggle.accordion-3 header-tag="header" class="p-1" role="tab">-->
<!--                                    <span>Management</span>-->
<!--                            </b-card-header>-->
<!--                            <b-collapse id="accordion-3" accordion="my-accordion" role="tabpanel">-->
<!--                                <b-card-body>-->
<!--                                    <b-card-text class="text-left">-->
<!--                                        <h6 class="accordion-heading">Impact of Improving Training on Annual Technician Revenue</h6>-->
<!--                                        <div class="row">-->
<!--                                            <div class="col-md-6">-->
<!--                                                <p>-->
<!--                                                    While the responsibilities between distinct management roles differ, their combined efforts significantly affect Technician satisfaction. Management at every level of the organization has a role in cultivating a supportive environment in which Technicians feel valued.-->
<!--                                                </p>-->
<!--                                                <p>-->
<!--                                                    For new Technicians, high-quality Managers are essential for building confidence and providing a positive first impression of the industry. First impressions are critical, and Technicians who have negative experiences early in their careers are more likely to leave for a position in another industry. As a result, the responsibility falls on Managers to create a rewarding experience from the very beginning.-->
<!--                                                </p>-->
<!--                                            </div>-->
<!--                                            <div class="col-md-6">-->
<!--                                                <div class="chart-container">-->
<!--                                                    <img width="100%" src='../assets/images/chart-placeholder.png' class="" alt="chart">-->
<!--                                                </div>-->
<!--                                            </div>-->
<!--                                        </div>-->
<!--                                        <div class="mt-4">-->
<!--                                            <p>-->
<!--                                                <strong>Recommendations</strong> in this section focus on creating positive, genuine relationships between service Managers and Technicians, as well as developing a framework to correct existing issues.  We recommend evaluating a dealership’s performance by focusing on the following areas:-->
<!--                                            </p>-->
<!--                                            <ul>-->
<!--                                                <li>-->
<!--                                                    <strong>Meeting Structure:</strong> Format and frequency of dealership meetings for all Technicians, aimed toward fostering positive relationships with Managers-->
<!--                                                </li>-->
<!--                                                <li>-->
<!--                                                    <strong>Error Policy:</strong> Procedures on how to handle errors in the dealership, maintaining respect for Technicians while still addressing problems-->
<!--                                                </li>-->
<!--                                                <li>-->
<!--                                                    <strong>Feedback Mechanisms:</strong> Systems to request and receive feedback from Technicians-->
<!--                                                </li>-->
<!--                                            </ul>-->
<!--                                        </div>-->
<!--                                    </b-card-text>-->
<!--                                </b-card-body>-->
<!--                            </b-collapse>-->
<!--                        </b-card>-->

<!--                        <b-card no-body class="mb-2">-->
<!--                            <b-card-header v-b-toggle.accordion-4 header-tag="header" class="p-1" role="tab">-->
<!--                                    <span>Dealer Conditions</span>-->
<!--                            </b-card-header>-->
<!--                            <b-collapse id="accordion-4" accordion="my-accordion" role="tabpanel">-->
<!--                                <b-card-body>-->
<!--                                    <b-card-text class="text-left">-->
<!--                                        <h6 class="accordion-heading">Impact of Improving Training on Annual Technician Revenue</h6>-->
<!--                                        <p>-->
<!--                                            Technicians are influenced heavily by the working conditions at a dealership. Working conditions refer to two factors specifically:-->
<!--                                        </p>-->
<!--                                        <p class="pl-4">-->
<!--                                            <strong>Physical Workspace:</strong> The facility, equipment, and tools that Technicians are provided within a dealership (i.e. breakroom and dealership cleanliness)-->
<!--                                        </p>-->
<!--                                        <p class="pl-4">-->
<!--                                            <strong>Dealership Culture:</strong> The environment and atmosphere that makes up a service department (i.e. relationships with co-workers)-->
<!--                                        </p>-->
<!--                                        <div class="row">-->
<!--                                            <div class="col-md-6">-->
<!--                                                <p>-->
<!--                                                    Dealerships may not recognize how critical these items are in attracting and retaining talent; however, the numbers illustrate their importance. 29% of all Technicians at dealerships selected their position because of the culture, while 10% join because of the quality of facility.-->
<!--                                                </p>-->
<!--                                            </div>-->
<!--                                            <div class="col-md-6">-->
<!--                                                <div class="chart-container">-->
<!--                                                    <img width="100%" src='../assets/images/chart-placeholder.png' class="" alt="chart">-->
<!--                                                </div>-->
<!--                                            </div>-->
<!--                                        </div>-->
<!--                                        <p>-->
<!--                                            <strong>Recommendations</strong> in this section emphasize improving the physical and cultural conditions of a dealership. We recommend that Managers evaluate the following areas:-->
<!--                                        </p>-->
<!--                                        <ul>-->
<!--                                            <li>-->
<!--                                                <strong>Dispatch Methods:</strong> Methods for distributing work to Technicians-->
<!--                                            </li>-->
<!--                                            <li>-->
<!--                                                <strong>Work Processes:</strong> Processes to maximize Technician productivity by increasing the amount of time an individual spends on their bay-->
<!--                                            </li>-->
<!--                                            <li>-->
<!--                                                <strong>Facilities Standards:</strong> Dealership guidelines for cleanliness and organization-->
<!--                                            </li>-->
<!--                                            <li>-->
<!--                                                <strong>Dealerships Culture:</strong> The culture of dealerships and relationships between the service department personnel-->
<!--                                            </li>-->
<!--                                        </ul>-->
<!--                                    </b-card-text>-->
<!--                                </b-card-body>-->
<!--                            </b-collapse>-->
<!--                        </b-card>-->

<!--                        <b-card no-body class="mb-2">-->
<!--                            <b-card-header v-b-toggle.accordion-5 header-tag="header" class="p-1" role="tab">-->
<!--                                    <span>Rewards & Recognition</span>-->
<!--                            </b-card-header>-->
<!--                            <b-collapse id="accordion-5" accordion="my-accordion" role="tabpanel">-->
<!--                                <b-card-body>-->
<!--                                    <b-card-text class="text-left">-->
<!--                                        <h6 class="accordion-heading">Impact of Improving Training on Annual Technician Revenue</h6>-->
<!--                                        <div class="row">-->
<!--                                            <div class="col-md-6">-->
<!--                                                <p>-->
<!--                                                    Many dealerships have formal recognition programs for experienced Technicians. However, few dealerships have policies in which new hires are eligible. Since the turnover of Technicians within the first 12-18 months is a critical issue, it is important to institute policies to recognize these individuals for their efforts.-->
<!--                                                </p>-->
<!--                                                <p>-->
<!--                                                    One in five Technicians select dealerships based on how Managers treat them.-->
<!--                                                </p>-->
<!--                                            </div>-->
<!--                                            <div class="col-md-6">-->
<!--                                                <div class="chart-container">-->
<!--                                                    <img width="100%" src='../assets/images/chart-placeholder.png' class="" alt="chart">-->
<!--                                                </div>-->
<!--                                            </div>-->
<!--                                        </div>-->
<!--                                        <div class="mt-4">-->
<!--                                            <p>-->
<!--                                                <strong>Recommendations</strong> in this section focus on establishing effective rewards and recognition programs. Through extensive industry research, we have uncovered programs that are effective and the reasons why Technicians care.  We recommend that dealerships focus on the following areas:-->
<!--                                            </p>-->
<!--                                            <ul>-->
<!--                                                <li>-->
<!--                                                    <strong>Rewards Programs and Special Bonuses:</strong> Initiatives and incentives that focus on acknowledging Technicians, as well as providing awards for their contribution to the dealership-->
<!--                                                </li>-->
<!--                                                <li>-->
<!--                                                    <strong>Workday Perks:</strong> Incentives offered to staff and communicate that Managers appreciate their work-->
<!--                                                </li>-->
<!--                                                <li>-->
<!--                                                    <strong>Tenure Benefits:</strong> Policies to acknowledge veteran Technicians for their commitment to the dealership and industry-->
<!--                                                </li>-->
<!--                                            </ul>-->
<!--                                        </div>-->
<!--                                    </b-card-text>-->
<!--                                </b-card-body>-->
<!--                            </b-collapse>-->
<!--                        </b-card>-->
<!--                    </div>-->
<!--                </mq-layout>-->
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: "Background",
        data () {
            return {
                num_percent: 50,
                num_decrease: 1.7,
                num_turnover: 0,
                show_calculator: false
            };
        },
        created(){
            this.calculateTurnover();
        },
        methods:{
            calculateTurnover(){
              // I know this isn't how percentages work, but I assure you this is how they want it; just multiply the numbers
                this.num_turnover = (this.num_decrease * this.num_percent).toFixed(2);
            },
            inc_percent(){
                this.num_percent++;
                this.calculateTurnover();
            },
            dec_percent(){
                this.num_percent--;
                this.calculateTurnover();
            }
        }
    }
</script>

<style lang="scss" scoped>

    #background {
      @media (max-width: 1100px) {
        padding-left: 5px;
        padding-right: 5px;
      }

        padding: 25px 30px 40px;
        .lost-revenue-copy {
            p {
                letter-spacing: .88px;
                line-height: 26px;
            }
        }
        .lost-revenue {
            padding-top: 50px;
            .lost-revenue-title {
                text-transform: uppercase;
                text-align: center;
                color: $fordBlue;
                .dealer-name {
                    @extend .lost-revenue-title;
                }
            }
            .lost-revenue-equation {
                display: flex;
                justify-content: center;
                align-items: center;
                flex-direction: column;
                .left, .center, .right {
                    width: 190px;
                    text-align: center;
                }
                .equation-flex-direction {
                    display: flex;
                    @media (max-width: 767px) {
                        flex-direction: column;
                        align-items: flex-end;
                        padding-right: 40px;
                    }
                    @media (max-width: 400px) {
                        padding-right: 20px;
                    }
                    .left, .right {
                        display: flex;
                        flex-direction: column;
                        .left-num, .right-num {
                            font-size: 28px;
                            font-family: "FordAntennaCond-Regular", sans-serif;
                        }
                    }
                    .left {
                        @media (min-width: 767px) {
                            width: 170px;
                        }
                        .left-num {
                            color: $fordBlue;
                        }
                    }
                    .right {
                        margin-left: 20px;
                        padding-left: 10px;
                        @media (max-width: 767px) {
                            margin-left: 0;
                        }
                        .r-top {
                            display: flex;
                            flex-direction: row;
                        }
                        .right-num {
                            color: #d0021b;
                        }
                        .equation-symbol {
                            right: 10px;
                            @media (max-width: 767px) {
                                right: 70px;
                            }
                        }
                    }
                    .center {
                        width: 235px;
                        display: flex;
                        flex-direction: column;
                        .c-top {
                            display: flex;
                            flex-direction: row;
                            .equation-symbol {
                                right: 15px;
                            }
                        }
                        .input-group {
                            max-width: 190px;
                            *:focus {
                                box-shadow: none;
                            }
                            input {
                                border-color: #6c757d;
                                text-align: center;
                                max-height: 46px;
                                font-size: 28px;
                                padding: 1px 2px;
                                padding-right: 30px;
                                color: $fordBlue;
                                font-family: "FordAntennaCond-Regular", sans-serif;
                                &[type=number]::-webkit-inner-spin-button,
                                &[type=number]::-webkit-outer-spin-button {
                                    -webkit-appearance: none;
                                    margin: 0;
                                }
                            }
                            button {
                                width: 50px;
                                background-color: #e8ecf0;
                                &:hover, &:focus {
                                    background-color: #e8ecf0;
                                }
                                svg {
                                    font-size: 20px;
                                    path {
                                        color: $fordBlue;
                                    }
                                }
                            }
                            .input-group-append:before {
                                content: "%";
                                position: absolute;
                                right: 65px;
                                top: 2px;
                                font-size: 28px;
                                z-index: 900;
                                color: $fordBlue;
                                font-family: "FordAntennaCond-Regular", sans-serif;
                            }
                        }
                        .equation-bottom-text {
                            margin-left: 45px;
                            margin-top: 6px;
                        }
                    }
                    .equation-symbol {
                        display: inline-flex;
                        align-items: center;
                        position: relative;
                        svg {
                            font-size: 40px;
                            path {
                                fill: #d1d3d4;
                            }
                        }
                    }
                    .turnover-rate {
                        width: 185px;
                    }
                    .equation-bottom-text {
                        font-size: 12px;
                        letter-spacing: 1px;
                        margin-top: 10px;
                    }
                }
            }
        }

        //Tabs Styles
        .tab-main-title {
            font-family: FordAntennaCond-Light, sans-serif;
            font-size: 26px;
            text-transform: uppercase;
            color: $fordBlue;
        }
        .accordion {
            .card-header {
                span {
                    height: 100%;
                    letter-spacing: .88px;
                }
            }
            .accordion-heading {
                text-transform: uppercase;
                color: $fordBlue;
                margin-bottom: 15px;
                letter-spacing: 1px;
            }
            .card-body {
                letter-spacing: .88px;
                line-height: 26px;
            }
        }
    }

</style>
